import { Helmet } from 'react-helmet';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BankIdIcon from 'assets/icons/ico-bankid.svg?react';
import { TwButton } from 'components/TwButton/TwButton';
import BankIDModal from 'pages/Registration/Intro/BankIDModal';
import { Card, Modal } from '../../../../components';
import useBankIdInfo from './useBankIdInfo';

const bankIdItems = [
  { title: 'box_row_1' },
  { title: 'box_row_2' },
  { title: 'box_row_3' },
] as const;

function TopSection({ handleButtonClick }: { handleButtonClick: () => void }) {
  const { t } = useTranslation(['bank_id', 'intro']);
  return (
    <div className="flex flex-col xl:flex-row items-center xl:items-start justify-between gap-6">
      <div className="flex flex-col gap-4">
        <h1 className="font-medium text-2xl text-center xl:text-left">
          {t('title')}
        </h1>
        <div className="flex flex-col">
          <p className="text-center xl:text-left">
            {t('subtitle')}
            <button
              className="text-primary"
              type="button"
              onClick={handleButtonClick}
            >
              {t('intro:more_information')}
            </button>
          </p>
        </div>
      </div>
      <BankIdIcon className="w-32 h-6 flex-shrink-0" />
    </div>
  );
}

function OrderedList() {
  const { t } = useTranslation('bank_id');
  return (
    <div className="p-5 bg-background-accent rounded-md w-full">
      <p className="font-semibold mb-4">{t('box_title')}</p>
      <ol className="flex flex-col gap-4">
        {bankIdItems.map(({ title }, index) => (
          <li key={t(title)} className="flex gap-4">
            <div className="flex justify-center items-start outline-2 outline border-primary rounded-full h-6 w-6 flex-shrink-0 bg-background text-primary font-semibold text-[15px]">
              {index + 1}
            </div>
            <div>{t(title)}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function BankIdInfo() {
  const { t } = useTranslation('bank_id');
  const [openedModal, setOpenedModal] = useState<boolean>(false);
  const { initBankId, handleSkip, isPending } = useBankIdInfo();

  const secondaryBtnColor = isPending ? 'muted' : 'primary';

  return (
    <Suspense>
      <div data-test="bank-id-info" className="flex flex-col">
        <Helmet>
          <title>{t('html_title')}</title>
        </Helmet>

        <Card className="flex flex-col gap-6 items-center rounded-none lg:rounded-lg">
          <TopSection handleButtonClick={() => setOpenedModal(true)} />
          <OrderedList />
          <div className="flex w-full justify-center lg:justify-start">
            <TwButton
              onClick={initBankId}
              data-test="continue"
              pending={isPending}
            >
              {t('continue')}
            </TwButton>
          </div>
        </Card>

        <div className="px-6 lg:px-0">
          <button
            type="button"
            className={`border border-${secondaryBtnColor} text-${secondaryBtnColor} mt-6 p-3 max-w-[420px] m-0 rounded-sm w-full uppercase mb-10`}
            data-test="skip"
            disabled={isPending}
            onClick={handleSkip}
          >
            {t('missing_id')}
          </button>
        </div>
      </div>
      {openedModal && (
        <Modal className="flex-col" onClose={() => setOpenedModal(false)}>
          <BankIDModal />
        </Modal>
      )}
    </Suspense>
  );
}

export default BankIdInfo;
